import { z } from 'zod';
import { amountInputSchema, amountSchema, nonNegativeAmountInputSchema } from '../common/amount.js';
import { paginationQuerySchema } from '../common/pagination.js';
import { isoDateTimeSchema } from '../common/primitives.js';
import { employeeRefSchema, recordStatusSchema } from '../common/refs.js';
import { transferCommissionModeSchema } from '../transfers/direction.js';
/**
 * Commission tiers — شرائح العمولة (transfers.md section 3).
 *
 * A tier has a name, an inclusive upper limit (null = no upper limit), a
 * fixed JOD commission (zero or more) and a status. Ranges are built from the
 * upper limits: tiers sorted by upper limit, each covering amounts greater
 * than the previous tier's upper limit up to and including its own. Only one
 * tier may have no upper limit, and two tiers never share an upper limit.
 */
export const tierNameSchema = z.string().trim().min(1).max(80);
/** POST /tiers and PATCH /tiers/:id. `upperLimit: null` means بلا حد. */
export const createTierSchema = z.object({
    name: tierNameSchema,
    upperLimit: amountInputSchema.nullable(),
    commission: nonNegativeAmountInputSchema,
});
export const updateTierSchema = createTierSchema.partial();
/**
 * GET /tiers — a standard paginated list sorted by upper limit (no limit
 * last); ranges are computed across every tier, not only the page.
 */
export const tierListQuerySchema = paginationQuerySchema.extend({
    status: recordStatusSchema.optional(),
});
export const tierViewSchema = z.object({
    id: z.string(),
    name: z.string(),
    /** Computed: the previous tier's upper limit (exclusive), "0.00000" for the first. */
    lowerLimit: amountSchema,
    /** Inclusive; null = no upper limit. */
    upperLimit: amountSchema.nullable(),
    commission: amountSchema,
    status: recordStatusSchema,
    /** A tier used by any transfer cannot be deleted. */
    used: z.boolean(),
    createdAt: isoDateTimeSchema,
    createdBy: employeeRefSchema,
    updatedAt: isoDateTimeSchema,
});
/**
 * A range of amounts without an active tier (disabled tier, or above the
 * highest limited tier when no unlimited tier is active). The page shows
 * "لا توجد شريحة فعالة للمبالغ من {from} إلى {to}"; `to: null` = and above.
 */
export const tierGapSchema = z.object({
    from: amountSchema,
    to: amountSchema.nullable(),
});
/** GET /tiers/gaps — the uncovered ranges shown as warnings on the tiers page (TRF-03). */
export const tierGapListSchema = z.array(tierGapSchema);
/**
 * GET /tiers/quote?amount=&commissionMode= — the commission for an amount from
 * the active tiers. The tier is always looked up on `amount`; with
 * `commissionMode=ADDED` (TRF-60) the answer's `amount` is amount + commission
 * (the cash received from the customer) and its `net` is the entered amount
 * (the amount transferred).
 */
export const tierQuoteQuerySchema = z.object({
    amount: amountInputSchema,
    commissionMode: transferCommissionModeSchema.optional(),
});
export const tierQuoteSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('OK'),
        /** What comes in: the entered amount, or amount + commission when ADDED. */
        amount: amountSchema,
        tier: z.object({
            id: z.string(),
            name: z.string(),
            lowerLimit: amountSchema,
            upperLimit: amountSchema.nullable(),
        }),
        commission: amountSchema,
        /** What goes out: amount − commission, or the entered amount when ADDED. */
        net: amountSchema,
    }),
    /** لا توجد شريحة لهذا المبلغ */
    z.object({ status: z.literal('NO_TIER'), amount: amountSchema }),
    /** The commission is equal to or greater than the amount [PRD-07]. */
    z.object({
        status: z.literal('COMMISSION_NOT_BELOW_AMOUNT'),
        amount: amountSchema,
        commission: amountSchema,
    }),
]);
//# sourceMappingURL=contract.js.map